import { useState, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Camera,
  Upload,
  Loader2,
  CheckCircle2,
  XCircle,
  User,
  Calendar,
  MapPin,
  CreditCard,
  RefreshCw
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IDScanner({ onDataExtracted, authHeader = {} }) {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (JPG, PNG, etc.)");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 10MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    // Upload and scan
    setIsScanning(true);
    setScanResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/documents/scan-id`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...authHeader
        }
      });

      setScanResult(response.data);
      
      if (response.data.full_name || response.data.date_of_birth) {
        toast.success("ID scanned successfully! Data extracted.");
        if (onDataExtracted) {
          onDataExtracted(response.data);
        }
      } else {
        toast.warning("Could not extract data from this image. Try a clearer photo.");
      }
    } catch (error) {
      console.error("Scan error:", error);
      toast.error(error.response?.data?.detail || "Failed to scan ID. Please try again.");
    } finally {
      setIsScanning(false);
    }
  };

  const handleRetry = () => {
    setScanResult(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const applyData = () => {
    if (scanResult && onDataExtracted) {
      onDataExtracted(scanResult);
      toast.success("Data applied to form!");
    }
  };

  return (
    <div className="space-y-4" data-testid="id-scanner">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Scan Your ID</Label>
        {scanResult && (
          <Button type="button" variant="ghost" size="sm" onClick={handleRetry}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Scan Again
          </Button>
        )}
      </div>

      <p className="text-sm text-gray-500">
        Upload a photo of your ID, Passport, or Driver's License to auto-fill your information.
      </p>

      {!previewUrl ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#5BB5A2] hover:bg-[#5BB5A2]/5 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="id-file-input"
          />
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="w-8 h-8 text-gray-400" />
          </div>
          <p className="font-medium text-[#0F3D3E] mb-1">
            Click to upload or take a photo
          </p>
          <p className="text-sm text-gray-500">
            Supports JPG, PNG, WebP (max 10MB)
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview */}
          <div className="relative rounded-xl overflow-hidden bg-gray-100">
            <img
              src={previewUrl}
              alt="ID Preview"
              className="w-full max-h-48 object-contain"
            />
            {isScanning && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center text-white">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm">Scanning ID...</p>
                </div>
              </div>
            )}
          </div>

          {/* Scan Results */}
          {scanResult && (
            <div className={`rounded-xl p-4 ${
              scanResult.confidence > 0.7 
                ? "bg-green-50 border border-green-200" 
                : "bg-amber-50 border border-amber-200"
            }`}>
              <div className="flex items-center gap-2 mb-3">
                {scanResult.confidence > 0.7 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-amber-600" />
                )}
                <span className="font-medium">
                  {scanResult.confidence > 0.7 ? "Data Extracted" : "Partial Data Extracted"}
                </span>
                <span className="text-xs text-gray-500 ml-auto">
                  {Math.round(scanResult.confidence * 100)}% confidence
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {scanResult.full_name && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-medium">{scanResult.full_name}</p>
                    </div>
                  </div>
                )}
                
                {scanResult.date_of_birth && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Date of Birth</p>
                      <p className="font-medium">{scanResult.date_of_birth}</p>
                      {scanResult.age && (
                        <p className="text-xs text-gray-500">({scanResult.age} years old)</p>
                      )}
                    </div>
                  </div>
                )}

                {scanResult.address && (
                  <div className="flex items-center gap-2 col-span-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="font-medium">{scanResult.address}</p>
                    </div>
                  </div>
                )}

                {scanResult.id_number && (
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">ID Number</p>
                      <p className="font-medium">{scanResult.id_number}</p>
                    </div>
                  </div>
                )}
              </div>

              {(scanResult.full_name || scanResult.age) && (
                <Button 
                  type="button"
                  className="w-full mt-4 btn-primary"
                  onClick={applyData}
                  data-testid="apply-id-data"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Use This Information
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
